import { ActionTree, MutationTree } from "vuex";
import EventsApi from "@/api/EventsApi";
import { Event, EventCategory, LocallySearchableAsset, IRootState } from "@iloq/shared";
import { handleCommonApiErrors } from "@/api/CommonApiErrorHandler";

const eventsApi = new EventsApi();

export interface IEventsState {
  events: Event[];
  filters: IFilters;
  filtered: IFilteredEventGroup[];
  cities: string[];
}

export interface IFilters {
  category: EventCategory | undefined;
  city: string | undefined;
}

export interface IFilteredEventGroup {
  year: string;
  month: string;
  events: Event[];
}

const initialState: IEventsState = {
  events: [],
  filters: {
    category: undefined,
    city: undefined,
  },
  filtered: [],
  cities: [],
};

const actions: ActionTree<IEventsState, IRootState> = {
  fetchEvents({ commit, dispatch }, params: { countryCode: string; languageCode: string }): void {
    eventsApi
      .getEvents(params.countryCode, params.languageCode)
      .then((data: Event[]) => {
        commit("setEvents", data);
        commit("pickCities");
        commit("filterEvents");
        dispatch(
          "globalSearch/refreshData",
          { assetType: LocallySearchableAsset.event, assets: data },
          { root: true },
        );
      })
      .catch(handleCommonApiErrors);
  },
  updateCategoryFilter({ commit }, category: EventCategory | undefined): void {
    commit("setCategoryFilter", category);
    commit("filterEvents");
  },
  updateCityFilter({ commit }, city: string | null): void {
    commit("setCityFilter", city);
    commit("filterEvents");
  },
};

const mutations: MutationTree<IEventsState> = {
  setEvents(state: IEventsState, events: Event[]): void {
    state.events = events;
  },
  setCategoryFilter(state: IEventsState, category: EventCategory | undefined): void {
    state.filters.category = category;
  },
  setCityFilter(state: IEventsState, city: string | undefined): void {
    state.filters.city = city;
  },
  pickCities(state: IEventsState): void {
    const uniqueCities = new Set();
    state.events.forEach((event: Event) => {
      uniqueCities.add(event.city);
    });
    state.cities = (Array.from(uniqueCities) as string[]).sort();
  },
  filterEvents(state: IEventsState): void {
    const result: IFilteredEventGroup[] = [];
    const groupsByYearAndMonth: { [key: string]: IFilteredEventGroup } = {};
    // Assumption: state.events contains events in chronological order by startTime
    state.events.forEach((event: Event) => {
      if (
        (state.filters.city === undefined || state.filters.city === event.city) &&
        (state.filters.category === undefined || state.filters.category === event.category)
      ) {
        const key = event.startTime.toISOString().substring(0, 7);
        let group: IFilteredEventGroup | undefined = groupsByYearAndMonth[key];
        if (!group) {
          const keyParts = key.split("-");
          group = {
            year: keyParts[0],
            month: keyParts[1],
            events: [],
          };
          groupsByYearAndMonth[key] = group;
          result.push(group);
        }
        group.events.push(event);
      }
    });
    state.filtered = result;
  },
};

export const eventsModule = {
  actions,
  mutations,
  state: initialState,
  namespaced: true,
};
